import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Space, Spin, Alert, Input } from 'antd';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import { EnvironmentOutlined, GlobalOutlined, SearchOutlined } from '@ant-design/icons';
import 'leaflet/dist/leaflet.css';

const { Title, Text } = Typography;
const { Search } = Input;

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

interface LocationMapProps {
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  title?: string;
  height?: number;
  showAddress?: boolean;
  onLocationSelect?: (lat: number, lng: number) => void;
  editable?: boolean;
}

// Component to handle map center updates
const MapUpdater: React.FC<{ latitude: number; longitude: number }> = ({ latitude, longitude }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView([latitude, longitude], 15);
  }, [latitude, longitude, map]);
  
  return null;
};

// Component to handle map click events
const MapClickHandler: React.FC<{ onMapClick: (event: any) => void }> = ({ onMapClick }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleClick = (event: any) => {
      onMapClick(event);
    };
    
    map.on('click', handleClick);
    
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);
  
  return null;
};

const LocationMap: React.FC<LocationMapProps> = ({
  latitude,
  longitude,
  address,
  title = 'Location',
  height = 400,
  showAddress = true,
  onLocationSelect,
  editable = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debug logging
  console.log('LocationMap props:', { latitude, longitude, address, title, editable });

  // Default coordinates (can be customized)
  const defaultLat = 40.7128;
  const defaultLng = -74.0060;

  const hasValidCoordinates = latitude && longitude && 
    !isNaN(latitude) && !isNaN(longitude) &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180;

  console.log('hasValidCoordinates:', hasValidCoordinates); // Debug log

  const handleMapClick = (event: any) => {
    if (editable && onLocationSelect) {
      const { lat, lng } = event.latlng;
      onLocationSelect(lat, lng);
    }
  };

  const handleGeocodeAddress = async () => {
    if (!address) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Geocoding address:', address);
      
      // Clean up the address for better geocoding results
      const cleanAddress = address
        .replace(/\s*,\s*USA\s*,\s*\d{5}/, '') // Remove ", USA, 12345" pattern
        .replace(/\s*\([^)]*\)/, '') // Remove parenthetical notes like "(Apt 307)"
        .trim();
      
      console.log('Cleaned address:', cleanAddress);
      
      // Try geocoding with a simple approach
      const coordinates = await trySimpleGeocoding(cleanAddress);
      
      if (coordinates) {
        console.log('Found coordinates:', coordinates.lat, coordinates.lon);
        if (onLocationSelect) {
          onLocationSelect(coordinates.lat, coordinates.lon);
          setError(null);
        }
      } else {
        setError(`Could not automatically geocode: "${cleanAddress}". Please click on the map to set coordinates manually, or use the edit dialog to enter coordinates.`);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setError(`Geocoding service unavailable. Please click on the map to set coordinates manually, or use the edit dialog to enter coordinates.`);
    } finally {
      setIsLoading(false);
    }
  };

  const trySimpleGeocoding = async (address: string) => {
    try {
      // Add a delay to respect rate limits
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=us`,
        {
          headers: {
            'User-Agent': 'PA-CRM-App/1.0'
          }
        }
      );
      
      if (!response.ok) {
        console.log('Geocoding service response not ok:', response.status);
        return null;
      }
      
      const data = await response.json();
      console.log('Geocoding response:', data);
      
      if (data && data.length > 0) {
        const location = data[0];
        return {
          lat: parseFloat(location.lat),
          lon: parseFloat(location.lon)
        };
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
    return null;
  };

  const handleSearchAddress = async (searchAddress: string) => {
    if (!searchAddress.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Searching for address:', searchAddress);
      
      const coordinates = await trySimpleGeocoding(searchAddress);
      
      if (coordinates) {
        console.log('Found coordinates for search:', coordinates.lat, coordinates.lon);
        if (onLocationSelect) {
          onLocationSelect(coordinates.lat, coordinates.lon);
          setError(null);
        }
      } else {
        setError(`Could not find: "${searchAddress}". Try a different search term or click on the map manually.`);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(`Search service unavailable. Please click on the map manually to set the location.`);
    } finally {
      setIsLoading(false);
    }
  };

  const openInMaps = () => {
    if (hasValidCoordinates) {
      const url = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15`;
      window.open(url, '_blank');
    }
  };

  const openInGoogleMaps = () => {
    if (hasValidCoordinates) {
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  };

  if (!hasValidCoordinates && !editable) {
    return (
      <Card title={title}>
        <div className="text-center py-8">
          <EnvironmentOutlined className="text-4xl text-gray-400 mb-4" />
          <Text type="secondary">No location coordinates available</Text>
          {address && (
            <div className="mt-4">
              <Text strong>Address:</Text> {address}
              <div className="mt-2">
                <Button 
                  type="primary" 
                  onClick={handleGeocodeAddress}
                  loading={isLoading}
                  icon={<GlobalOutlined />}
                >
                  Try to Get Coordinates from Address
                </Button>
              </div>
              <div className="mt-2">
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  If automatic geocoding fails, use the edit button (pencil icon) to manually set coordinates on the map.
                </Text>
              </div>
            </div>
          )}
          {error && (
            <Alert
              message={error}
              type="warning"
              showIcon
              className="mt-4"
              description={
                <div>
                  <Text>To set coordinates manually:</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Click the edit button (pencil icon) in the Claim Address & Location section</li>
                    <li>In the edit dialog, click on the map to set the exact location</li>
                    <li>Or type coordinates directly in the Latitude/Longitude fields</li>
                  </ul>
                </div>
              }
            />
          )}
        </div>
      </Card>
    );
  }

  const mapCenter = hasValidCoordinates 
    ? [latitude!, longitude!] 
    : [defaultLat, defaultLng];

  return (
    <Card 
      title={
        <Space>
          <EnvironmentOutlined />
          {title}
          {hasValidCoordinates && (
            <Space>
              <Button 
                size="small" 
                onClick={openInMaps}
                icon={<GlobalOutlined />}
              >
                OpenStreetMap
              </Button>
              <Button 
                size="small" 
                onClick={openInGoogleMaps}
                icon={<GlobalOutlined />}
              >
                Google Maps
              </Button>
            </Space>
          )}
        </Space>
      }
    >
      <div style={{ height, position: 'relative' }}>
        <MapContainer
          center={mapCenter as [number, number]}
          zoom={hasValidCoordinates ? 15 : 10}
          style={{ height: '100%', width: '100%' }}
        >
          {editable && (
            <MapClickHandler onMapClick={handleMapClick} />
          )}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {hasValidCoordinates && (
            <>
              <Marker position={[latitude!, longitude!]}>
                <Popup>
                  <div>
                    <Text strong>{title}</Text>
                    {showAddress && address && (
                      <div className="mt-2">
                        <Text type="secondary">{address}</Text>
                      </div>
                    )}
                    <div className="mt-2">
                      <Text type="secondary">
                        Coordinates: {latitude?.toFixed(6)}, {longitude?.toFixed(6)}
                      </Text>
                    </div>
                  </div>
                </Popup>
              </Marker>
              <MapUpdater latitude={latitude!} longitude={longitude!} />
            </>
          )}
        </MapContainer>
        
        {editable && !hasValidCoordinates && (
          <div className="absolute top-4 left-4 bg-white p-2 rounded shadow z-[1000] min-w-[300px]">
            <Text type="secondary" className="block mb-2">Search for an address or click on the map</Text>
            <Search
              placeholder="Enter address to search..."
              enterButton={<SearchOutlined />}
              size="small"
              onSearch={handleSearchAddress}
              loading={isLoading}
            />
          </div>
        )}
        
        {editable && hasValidCoordinates && (
          <div className="absolute top-4 left-4 bg-white p-2 rounded shadow z-[1000] min-w-[300px]">
            <Text type="secondary" className="block mb-2">Search for a different location or click to update</Text>
            <Search
              placeholder="Enter address to search..."
              enterButton={<SearchOutlined />}
              size="small"
              onSearch={handleSearchAddress}
              loading={isLoading}
            />
          </div>
        )}
        
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}
      </div>
      
      {showAddress && address && (
        <div className="mt-4">
          <Text strong>Address:</Text> {address}
        </div>
      )}
      
      {hasValidCoordinates && (
        <div className="mt-2">
          <Text type="secondary">
            Coordinates: {latitude?.toFixed(6)}, {longitude?.toFixed(6)}
          </Text>
        </div>
      )}
      
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="mt-4"
        />
      )}
    </Card>
  );
};

export default LocationMap; 